import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Sparkles, ArrowRight, ArrowLeft, ChevronRight, Zap, Trophy, Crown, Loader2, Lock, Mail, Building2, AtSign, KeyRound, User } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PLAN_VISUALS = {
  Starter: {
    icon: <Zap className="w-5 h-5" />,
    accent: "#9AE6B4",
    chip: "Best for cafés & solo restaurants",
  },
  Pro: {
    icon: <Trophy className="w-5 h-5" />,
    accent: "#E5A93C",
    chip: "Most popular · 14 of 20 sign-ups",
  },
  Elite: {
    icon: <Crown className="w-5 h-5" />,
    accent: "#C084FC",
    chip: "Add an AI Sommelier",
  },
};

const fmtInr = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [signupFor, setSignupFor] = useState(null); // plan object

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/public/plans");
        if (!alive) return;
        setPlans(data.plans || []);
        setTrialDays(data.trial_days || 30);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen relative overflow-hidden text-white" style={{ background: "#07080B" }}>
      {/* Background flourish — soft layered glows + grain */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(229,169,60,0.22), transparent)" }} />
        <div className="absolute top-1/2 -left-40 w-[520px] h-[520px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(192,132,252,0.16), transparent)" }} />
        <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-[700px] h-[300px] rounded-full" style={{ background: "radial-gradient(closest-side, rgba(154,230,180,0.14), transparent)" }} />
      </div>

      {/* Top nav */}
      <header className="relative z-20 max-w-7xl mx-auto flex items-center justify-between px-6 md:px-12 py-6">
        <Link to="/" className="flex items-center gap-2" data-testid="pricing-brand">
          <Logo size={36} />
          <span className="font-display text-2xl tracking-tight">Mnu<span className="gold-text">Verse</span></span>
        </Link>
        <nav className="flex items-center gap-2 text-sm">
          <Link to="/" className="px-3 py-1.5 text-white/70 hover:text-white inline-flex items-center gap-1" data-testid="pricing-back-home"><ArrowLeft className="w-3.5 h-3.5" /> Home</Link>
          <Link to="/why-mnuverse" className="px-3 py-1.5 text-white/70 hover:text-white" data-testid="pricing-why">Why MnuVerse</Link>
          <Link to="/login/admin" className="px-3 py-1.5 text-white/70 hover:text-white" data-testid="pricing-login">Sign in</Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center pt-6 pb-14">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 text-xs text-white/70 font-mono uppercase tracking-[0.2em]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#E5A93C]" /> Pricing · Pay yearly, save the paper menu
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-6">
            One menu OS. <span className="italic gold-text">Three ways to grow.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-white/65 max-w-2xl mx-auto leading-relaxed">
            Pick a plan, get <b className="text-white">{trialDays} days free</b>, no card needed. Cancel any time before the trial ends and you owe us nothing — not even a goodbye email.
          </p>
        </motion.div>
      </section>

      {/* Plans grid */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-16">
        {loading ? (
          <div className="flex items-center justify-center text-white/50 py-20"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading plans…</div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((p, idx) => (
              <PlanCard key={p.id} plan={p} idx={idx} trialDays={trialDays} onPick={() => setSignupFor(p)} />
            ))}
          </div>
        )}

        {/* Trust strip */}
        <div className="mt-12 grid sm:grid-cols-3 gap-4 text-sm text-white/70" data-testid="pricing-trust">
          <Trust title={`${trialDays}-day free trial`} body="No card on file. We'll email you 3 days before it ends." />
          <Trust title="Switch any time" body="Move up or down without losing your menu, photos, or orders." />
          <Trust title="Owned by you" body="Export menu + customer CRM as CSV at any moment." />
        </div>

        {/* FAQ */}
        <FAQ trialDays={trialDays} />
      </section>

      <footer className="relative z-10 border-t border-white/5 py-8 text-center text-xs text-white/40 font-mono">
        MnuVerse — Restaurant Operating System · <Link to="/" className="hover:text-white/70">mnuverse.in</Link>
      </footer>

      <AnimatePresence>
        {signupFor && (
          <SignupModal
            plan={signupFor}
            trialDays={trialDays}
            onClose={() => setSignupFor(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function PlanCard({ plan, idx, trialDays, onPick }) {
  const visuals = PLAN_VISUALS[plan.id] || PLAN_VISUALS.Pro;
  const highlight = plan.highlight;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.06 * idx }}
      whileHover={{ y: -4 }}
      data-testid={`plan-card-${plan.id}`}
      className={`relative rounded-3xl overflow-hidden transition-all ${highlight ? "md:-mt-4 md:mb-4" : ""}`}
      style={{
        background: highlight ? "linear-gradient(165deg, rgba(229,169,60,0.18), rgba(20,16,8,0.92) 60%)" : "linear-gradient(165deg, rgba(255,255,255,0.045), rgba(15,17,21,0.96) 60%)",
        border: `1px solid ${highlight ? "rgba(229,169,60,0.45)" : "rgba(255,255,255,0.08)"}`,
        boxShadow: highlight ? "0 30px 60px -30px rgba(229,169,60,0.4)" : "0 25px 50px -25px rgba(0,0,0,0.6)",
      }}
    >
      {highlight && (
        <div className="absolute top-4 right-4 text-[10px] font-mono uppercase tracking-[0.2em] px-2.5 py-1 rounded-full" style={{ background: "rgba(229,169,60,0.95)", color: "#0A0A0C" }}>
          Most loved
        </div>
      )}

      <div className="p-7">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full inline-flex items-center justify-center" style={{ background: `${visuals.accent}1f`, color: visuals.accent, border: `1px solid ${visuals.accent}55` }}>
            {visuals.icon}
          </span>
          <div className="font-display text-2xl">{plan.name}</div>
        </div>
        <div className="text-xs mt-2 font-mono uppercase tracking-[0.18em]" style={{ color: visuals.accent }}>{visuals.chip}</div>
        <p className="text-sm text-white/65 mt-3">{plan.tagline}</p>

        <div className="mt-7 flex items-baseline gap-1">
          <span className="font-display text-5xl" data-testid={`plan-price-${plan.id}`}>{fmtInr(plan.price_inr)}</span>
          <span className="text-sm text-white/50">/year</span>
        </div>
        <div className="mt-1 text-[11px] font-mono uppercase tracking-widest text-white/40">~{fmtInr(Math.round(plan.price_inr / 12))}/month, billed annually</div>

        <button
          onClick={onPick}
          data-testid={`start-trial-${plan.id}`}
          className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-semibold text-sm transition-all`}
          style={{
            background: highlight ? "var(--accent, #E5A93C)" : "transparent",
            color: highlight ? "#0A0A0C" : visuals.accent,
            border: `1px solid ${visuals.accent}`,
          }}
        >
          Start {trialDays}-day free trial <ArrowRight className="w-4 h-4" />
        </button>

        <div className="mt-7 space-y-2">
          {plan.features.map((f) => (
            <div key={f} className="flex items-start gap-2 text-sm">
              <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: visuals.accent }} />
              <span className="text-white/85">{f}</span>
            </div>
          ))}
          {(plan.not_included || []).map((f) => (
            <div key={f} className="flex items-start gap-2 text-sm">
              <X className="w-4 h-4 mt-0.5 shrink-0 text-white/25" />
              <span className="text-white/35 line-through">{f}</span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

function Trust({ title, body }) {
  return (
    <div className="p-4 rounded-2xl border border-white/8 bg-white/[0.02]">
      <div className="text-sm font-semibold flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-[#E5A93C]" /> {title}</div>
      <div className="text-xs text-white/55 mt-1.5">{body}</div>
    </div>
  );
}

function FAQ({ trialDays }) {
  const items = useMemo(() => [
    { q: "What happens after the free trial ends?", a: `On day ${trialDays + 1} we email you with a checkout link. Nothing auto-charges. If you don't pay, your dashboard goes read-only for 14 days while we hold your data, then we archive it.` },
    { q: "Can I change plans later?", a: "Yes, from Settings → Billing in your Owner dashboard. Upgrades take effect immediately and are pro-rated; downgrades apply on your next renewal." },
    { q: "Do I need a credit card to start the trial?", a: "Nope. We only ask for one when you decide to upgrade after the trial. No silent charges, ever." },
    { q: "Is there a setup fee?", a: "None. Your QR codes generate inside the dashboard the second the trial starts." },
    { q: "Which POS does the Pro plan integrate with?", a: "Petpooja is our reference integration. Toast, Square, PosBytz, Poster are on the roadmap — tell us yours and we'll prioritize it." },
  ], [trialDays]);
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section className="mt-16 max-w-3xl mx-auto" data-testid="pricing-faq">
      <h2 className="font-display text-3xl mb-5 text-center">Questions, neatly answered.</h2>
      <div className="rounded-2xl border border-white/8 divide-y divide-white/5 bg-white/[0.02]">
        {items.map((it, i) => {
          const open = i === openIdx;
          return (
            <button
              key={it.q}
              onClick={() => setOpenIdx(open ? -1 : i)}
              data-testid={`faq-${i}`}
              className="w-full text-left px-5 py-4"
            >
              <div className="flex items-center justify-between">
                <div className="font-semibold text-sm">{it.q}</div>
                <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90 text-[#E5A93C]" : "text-white/40"}`} />
              </div>
              {open && <div className="text-sm text-white/65 mt-2 leading-relaxed">{it.a}</div>}
            </button>
          );
        })}
      </div>
    </section>
  );
}

function slugifyName(name) {
  return (name || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 42)
    .replace(/^-|-$/g, "");
}

function SignupModal({ plan, trialDays, onClose }) {
  const nav = useNavigate();
  const { loginWithToken } = useAuth();
  const [form, setForm] = useState({
    restaurant_name: "",
    slug: "",
    owner_name: "",
    owner_email: "",
    owner_password: "",
    cuisine: "Contemporary",
  });
  const [slugTouched, setSlugTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Auto-fill slug from restaurant name until the user edits it explicitly.
  useEffect(() => {
    if (!slugTouched) {
      setForm((f) => ({ ...f, slug: slugifyName(f.restaurant_name) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.restaurant_name]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.restaurant_name.trim() || !form.slug.trim() || !form.owner_name.trim() || !form.owner_email.trim() || !form.owner_password) {
      toast.error("Please fill all fields"); return;
    }
    if (form.owner_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/trial-signup", {
        restaurant_name: form.restaurant_name.trim(),
        slug: form.slug.trim().toLowerCase(),
        plan: plan.id,
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim(),
        owner_password: form.owner_password,
        cuisine: form.cuisine,
      });
      loginWithToken(data.token, data.user);
      toast.success(`Welcome to MnuVerse — ${trialDays}-day trial started`);
      nav("/owner");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start trial");
    } finally { setSubmitting(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="trial-signup-modal"
    >
      <motion.form
        initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, y: 12 }}
        onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-md rounded-3xl p-6 max-h-[92vh] overflow-y-auto"
        style={{ background: "linear-gradient(165deg, rgba(20,18,12,0.97), rgba(10,10,12,0.99))", border: "1px solid rgba(229,169,60,0.3)" }}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Starting {trialDays}-day trial</div>
            <div className="font-display text-2xl mt-1">{plan.name} <span className="text-white/40 text-base">· {fmtInr(plan.price_inr)}/yr after</span></div>
          </div>
          <button type="button" onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10" data-testid="close-signup-modal"><X className="w-4 h-4" /></button>
        </div>

        <Field icon={<Building2 className="w-4 h-4" />} label="Restaurant name" testid="signup-restaurant-name"
          value={form.restaurant_name}
          onChange={(v) => setForm({ ...form, restaurant_name: v })}
          placeholder="The Gilded Fork"
        />

        <div className="mt-3">
          <label className="text-[10px] font-mono uppercase tracking-widest text-white/50">Your menu URL</label>
          <div className="flex items-stretch mt-1 rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
            <span className="px-3 py-2.5 text-xs text-white/40 font-mono bg-white/[0.04] inline-flex items-center">mnuverse.in/r/</span>
            <input
              value={form.slug}
              onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }); }}
              data-testid="signup-slug"
              placeholder="gilded-fork"
              className="flex-1 bg-transparent px-2 py-2.5 text-sm focus:outline-none"
            />
          </div>
          <div className="text-[10px] text-white/40 mt-1">Lowercase letters, numbers and dashes. This will be your QR code link.</div>
        </div>

        <Field icon={<User className="w-4 h-4" />} className="mt-3" label="Your name" testid="signup-owner-name"
          value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} placeholder="Celine Marchetti"
        />

        <Field icon={<AtSign className="w-4 h-4" />} className="mt-3" label="Work email" testid="signup-owner-email" type="email"
          value={form.owner_email} onChange={(v) => setForm({ ...form, owner_email: v })} placeholder="you@yourplace.com"
        />

        <Field icon={<KeyRound className="w-4 h-4" />} className="mt-3" label="Password" testid="signup-owner-password" type="password"
          value={form.owner_password} onChange={(v) => setForm({ ...form, owner_password: v })} placeholder="At least 6 characters"
        />

        <button
          type="submit"
          disabled={submitting}
          data-testid="confirm-trial-signup"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-semibold text-sm gold-btn disabled:opacity-60"
        >
          {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating your trial…</> : <>Start my {trialDays}-day trial <ArrowRight className="w-4 h-4" /></>}
        </button>

        <div className="text-[10px] text-white/40 mt-3 leading-relaxed flex items-start gap-1.5">
          <Lock className="w-3 h-3 mt-0.5 shrink-0" />
          No credit card. We email you 3 days before the trial ends. By starting you agree to our <Link to="/" className="underline">terms</Link>.
        </div>
      </motion.form>
    </motion.div>
  );
}

function Field({ icon, label, value, onChange, type = "text", placeholder, className = "", testid }) {
  return (
    <div className={className}>
      <label className="text-[10px] font-mono uppercase tracking-widest text-white/50">{label}</label>
      <div className="mt-1 flex items-center gap-2 rounded-xl px-3 py-0" style={{ border: "1px solid rgba(255,255,255,0.1)" }}>
        <span className="text-white/40">{icon}</span>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          className="flex-1 bg-transparent py-2.5 text-sm focus:outline-none"
        />
      </div>
    </div>
  );
}
